import { getAuth } from 'firebase/auth';

export interface WaitlistSubmissionInput {
  email: string;
  firstName?: string;
  messageText?: string;
  source?: string;
  linkedinUrl?: string;
  xHandle?: string;
}

const DEFAULT_PROJECT_ID = 'tensile-tenure-453409-k3';
const FUNCTION_NAME = 'submitPublicConstructionWaitlist';
const WAITLIST_PATH = '/api/waitlist/construction';
const PROD_HOSTNAMES = new Set([
  'www.projectolivia.tech',
  'projectolivia.tech',
  'www.prest0.ai',
  'prest0.ai',
  'projectolivia.web.app',
  'projectolivia.firebaseapp.com',
]);

function projectId(): string {
  try {
    const auth = getAuth();
    const app = auth.app as any;
    return app?.options?.projectId || process.env.REACT_APP_FIREBASE_PROJECT_ID || DEFAULT_PROJECT_ID;
  } catch {
    return process.env.REACT_APP_FIREBASE_PROJECT_ID || DEFAULT_PROJECT_ID;
  }
}

function shouldUseEmulator(): boolean {
  const flag = (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR || '').trim().toLowerCase();
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  try {
    if (typeof window !== 'undefined') {
      const globalFlag = (window as any).__USE_FIREBASE_FUNCTIONS_EMULATOR__;
      if (typeof globalFlag === 'boolean') return globalFlag;
    }
  } catch {}
  return false;
}

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

function runtimeOverrideEndpoint(): string | null {
  try {
    if (typeof window !== 'undefined') {
      const override = (window as any).__WAITLIST_ENDPOINT__;
      if (typeof override === 'string' && override.trim()) {
        return normalizeBaseUrl(override.trim());
      }
    }
  } catch {}
  return null;
}

function productionSameOriginEndpoint(): string | null {
  try {
    if (typeof window === 'undefined' || !window.location) return null;
    const { origin, hostname } = window.location;
    if (!origin || !hostname) return null;
    if (PROD_HOSTNAMES.has(hostname)) {
      return `${normalizeBaseUrl(origin)}${WAITLIST_PATH}`;
    }
  } catch {}
  return null;
}

function buildEndpoints(): string[] {
  const endpoints: string[] = [];
  const runtimeEndpoint = runtimeOverrideEndpoint();
  if (runtimeEndpoint) endpoints.push(runtimeEndpoint);

  const explicit = (process.env.REACT_APP_WAITLIST_ENDPOINT || '').trim();
  if (explicit) endpoints.push(normalizeBaseUrl(explicit));

  const sameOrigin = productionSameOriginEndpoint();
  if (sameOrigin) endpoints.push(sameOrigin);

  const baseOverride = (process.env.REACT_APP_FUNCTIONS_BASE || '').trim();
  if (baseOverride) endpoints.push(`${normalizeBaseUrl(baseOverride)}/${FUNCTION_NAME}`);

  const pid = projectId();
  const prodEndpoint = `https://us-central1-${pid}.cloudfunctions.net/${FUNCTION_NAME}`;
  endpoints.push(prodEndpoint);

  if (shouldUseEmulator()) {
    endpoints.unshift(`http://127.0.0.1:5001/${pid}/us-central1/${FUNCTION_NAME}`);
  }

  // Remove duplicates while preserving order
  return endpoints.filter((url, index) => endpoints.indexOf(url) === index);
}

async function postJson(endpoint: string, payload: Record<string, any>): Promise<any> {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch((error) => {
    throw new Error(String(error?.message || 'network error'));
  });

  let data: any = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok || (data && data.ok === false)) {
    const code = String(data?.error || '').toUpperCase();
    let message = String(data?.message || '').trim();
    if (!message) {
      if (code === 'EMAIL_REQUIRED') message = 'email address is required';
      else if (code === 'EMAIL_INVALID') message = 'enter a valid email address';
    }
    if (!message) message = `request failed with status ${res.status}`;
    const error = new Error(message);
    (error as any).code = code || `HTTP_${res.status}`;
    throw error;
  }

  return data;
}

export async function submitConstructionWaitlist(input: WaitlistSubmissionInput): Promise<void> {
  const email = String(input.email || '').trim().toLowerCase();
  if (!email) {
    throw new Error('email address is required');
  }

  const uid = (() => {
    try {
      return getAuth().currentUser?.uid || null;
    } catch {
      return null;
    }
  })();

  const payload = {
    email,
    firstName: input.firstName || undefined,
    messageText: input.messageText || undefined,
    source: input.source || 'prompt_bar',
    linkedinUrl: input.linkedinUrl || undefined,
    xHandle: input.xHandle || undefined,
    uid: uid || undefined,
  };

  const endpoints = buildEndpoints();
  let lastError: any = null;
  for (const endpoint of endpoints) {
    try {
      await postJson(endpoint, payload);
      return;
    } catch (error: any) {
      lastError = error;
      // Try the next endpoint if available
    }
  }

  throw lastError || new Error('unable to save your wait list request right now');
}

